import { useSelector } from 'react-redux';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Alert, Button, Skeleton } from 'antd';
import { DrawingManager, GoogleMap, Marker } from '@react-google-maps/api';
import { collect } from 'collect.js';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import DeliveryZone from './DeliveryZone';
import DeliveryZoneGeometry from './DeliveryZoneGeometry';
import { DELIVERY_ZONE_GEOMETRY_TYPES, DeliveryMode } from '../../../services/exports/Constants';
import useApiClient from '../../../hooks/api/useApiClient';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';
import useGoogleMaps from '../../../hooks/utility/useGoogleMaps';

const DeliveryZones = forwardRef(({ deliveryMode }, ref) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Delivery:DeliveryZones' });

  const { company } = useSelector((state) => state.currentCompany);

  const { DeliveryZonesManager } = useApiClient();
  const { can } = usePermissions();
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useGoogleMaps();

  useImperativeHandle(ref, () => ({
    requestDeliveryZones,
  }));

  const isReadonly = deliveryMode === DeliveryMode.External && !can(ManagerPermission.ManageExternalDelivery);

  const [initialZones, setInitialZones] = useState([]);
  const [zones, setZones] = useState([]);
  const [center, setCenter] = useState({
    lat: company?.lat ?? 50.7075413,
    lng: company?.lon ?? 7.1141516,
  });

  const [selectedZoneIndex, setSelectedZoneIndex] = useState(0);
  const [selectedZone, setSelectedZone] = useState(null);

  const updateInitialZone = (index, data) => setInitialZones((prev) => collect(prev).put(index, data).toArray());

  const resetZoneChanges = (index) => setZones((prev) => collect(prev).put(index, initialZones[index]).toArray());

  const updateZone = (index, field, value) =>
    setZones((prev) =>
      collect(prev)
        .put(index, {
          ...prev[index],
          [field]: value,
        })
        .toArray(),
    );

  const addZone = () => {
    const data = {
      id: null,
      name: t('stubs.name', { number: zones.length + 1 }),
      color: getDefaultColor(),
      min_threshold: 0,
      max_threshold: 0,
      fee: 0,
      geometry_type: DELIVERY_ZONE_GEOMETRY_TYPES.CIRCLE,
      is_hidden: false,
    };

    setZones((prev) => collect(prev).push(data).toArray());
    setInitialZones((prev) =>
      collect(prev)
        .push({ ...data })
        .toArray(),
    );
  };

  const deleteZone = (index) => {
    setZones((prev) => {
      const sliced = collect(prev);
      sliced.splice(index, 1);

      return sliced.toArray();
    });
    setInitialZones((prev) => {
      const sliced = collect(prev);
      sliced.splice(index, 1);

      return sliced.toArray();
    });
  };

  const getDefaultColor = () => {
    switch (zones?.length) {
      case 0:
        return '#69CE8C';
      case 1:
        return '#FC743A';
      default:
        return '#f2AC1F';
    }
  };

  const zoneRefs = useMemo(() => zones.map((item) => React.createRef(item)), [zones]);
  const drawingManagerRef = useRef();
  const drawingManager = drawingManagerRef?.current?.state?.drawingManager;

  const isGeometryComplete = (data) => {
    if (data?.geometry_type === DELIVERY_ZONE_GEOMETRY_TYPES.CIRCLE) {
      return (
        data?.geometry_data &&
        data?.geometry_data?.radius &&
        data?.geometry_data?.center &&
        data?.geometry_data?.center?.lat &&
        data?.geometry_data?.center?.lng
      );
    }

    return (
      data?.geometry_data &&
      data?.geometry_data?.paths &&
      collect(data?.geometry_data?.paths).every((item) => item?.lat && item?.lng)
    );
  };

  const getDrawingMode = (data) => {
    if (isGeometryComplete(selectedZone)) {
      return null;
    }

    switch (data?.geometry_type) {
      case DELIVERY_ZONE_GEOMETRY_TYPES.CIRCLE:
        return DELIVERY_ZONE_GEOMETRY_TYPES.CIRCLE;
      case DELIVERY_ZONE_GEOMETRY_TYPES.POLYGON:
        return DELIVERY_ZONE_GEOMETRY_TYPES.POLYGON;
      default:
        return null;
    }
  };

  useEffect(() => {
    setSelectedZone(collect(zones).get(selectedZoneIndex));
  }, [zones, selectedZoneIndex]);

  useEffect(() => {
    if (!drawingManager) {
      return;
    }

    drawingManager.setDrawingMode(getDrawingMode(selectedZone));
  }, [selectedZone]);

  useEffect(() => {
    requestDeliveryZones();
    setCenter({
      lat: company?.lat ?? 50.7075413,
      lng: company?.lon ?? 7.1141516,
    });
  }, []);

  const selectedZoneGeometryOptions = {
    draggable: true,
    editable: true,
    fillColor: selectedZone?.color ?? '#69CE8C',
    fillOpacity: 0.4,
    strokeColor: selectedZone?.color ?? '#69CE8C',
    strokeWeight: 2,
  };

  const prepareGeometryData = (type, geometry, fallback = null) => {
    const isZip = type === DELIVERY_ZONE_GEOMETRY_TYPES.ZIP;
    const isCircle = type === DELIVERY_ZONE_GEOMETRY_TYPES.CIRCLE;

    // A mounted Maps circle can report no center (seen in production on save of an untouched
    // zone); the zone state is kept in sync on every center/radius change, so it is a safe source.
    function handleCircle() {
      const geometryCenter = geometry?.getCenter?.();

      if (!geometryCenter) {
        return fallback;
      }

      return {
        center: {
          lat: geometryCenter.lat(),
          lng: geometryCenter.lng(),
        },
        radius: geometry.getRadius(),
      };
    }

    function handlePolygon() {
      return geometry
        ? {
            paths: geometry.getPath().getArray(),
          }
        : null;
    }

    if (isZip) {
      return collect(geometry).filter().toArray();
    }

    return isCircle ? handleCircle() : handlePolygon();
  };

  async function requestDeliveryZones() {
    const { success, data } = await DeliveryZonesManager.get();

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    setZones(data);

    return setInitialZones([...data]);
  }

  const onOverlayComplete = (event) => {
    updateZone(selectedZoneIndex, 'geometry_data', prepareGeometryData(selectedZone?.geometry_type, event.overlay));

    const zoneRef = zoneRefs[selectedZoneIndex] ?? null;
    if (!zoneRef) {
      return;
    }

    zoneRef.current?.setMap(null);
    zoneRef.current = event.overlay;
    zoneRef.current.addListener('dblclick', (event) => {
      event.stop();
      if (typeof event.path === 'undefined') {
        zoneRef.current.setMap(null);
        zoneRef.current = null;
      }
    });

    event.overlay.setMap(null);
  };

  return (
    <div>
      <div className="flex flex-wrap">
        <div className="w-full md:w-3/4">
          {mapLoadError && <Alert type="warning" showIcon message={t('map_unavailable')} />}
          {!mapLoadError && !isMapLoaded && <Skeleton.Node active className="!w-full !h-[600px]" />}
          {isMapLoaded && (
            <GoogleMap
              mapContainerStyle={{
                height: '600px',
                width: '100%',
              }}
              center={center}
              zoom={12}
              options={{
                mapTypeControl: false,
                streetViewControl: false,
              }}
            >
              <DrawingManager
                ref={drawingManagerRef}
                onOverlayComplete={onOverlayComplete}
                options={{
                  drawingControl: false,
                  drawingControlOptions: {
                    position: 5.0,
                    drawingModes: ['circle', 'polygon'],
                  },
                  circleOptions: selectedZoneGeometryOptions,
                  polygonOptions: selectedZoneGeometryOptions,
                }}
              />
              <Marker position={center} label={company?.name} />
              {zones.map((item, index) => (
                <DeliveryZoneGeometry
                  data={item}
                  onChange={(data) => updateZone(index, 'geometry_data', data)}
                  prepareData={prepareGeometryData}
                  forwardRef={zoneRefs[index]}
                  disabled={isReadonly}
                  key={`delivery-zone-geometry-${index}`}
                />
              ))}
            </GoogleMap>
          )}
        </div>
        <div className="w-full md:w-1/4 md:pl-md">
          {zones.map((item, index) => (
            <DeliveryZone
              data={item}
              geometryRef={zoneRefs[index]}
              prepareGeometryData={prepareGeometryData}
              updateInitialZone={(data) => updateInitialZone(index, data)}
              resetChanges={() => resetZoneChanges(index)}
              onChange={(field, value) => updateZone(index, field, value)}
              onSelect={() => setSelectedZoneIndex(index)}
              onDelete={() => deleteZone(index)}
              companyDeliveryMode={deliveryMode}
              isReadonly={isReadonly}
              index={index}
              className={classnames({ 'mt-2': index > 0 })}
              key={`delivery-zone-data-${index}`}
            />
          ))}
          {!isReadonly && (
            <Button type="link" onClick={addZone} className="mt-xs px-none">
              {t('buttons.add_new_zone')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default DeliveryZones;
